// TodayBar.tsx — 📋 Tänään-palkki joka ERP-sivulle (19.8.2026).
// Näyttää päivän työt/tavoitteet viikkosuunnitelmasta (sis. 📢-viestit)
// OperatorShellin yläosassa — sama sisältö kuin infonäytön Tänään-rivillä.
// Kevyt: /info-display haetaan 5 min välein + sessionStorage-siemen, jotta
// sivunvaihdot eivät aiheuta uusia hakuja. Klikkaus vie infonäytölle.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../api/client';

interface TodayTask { text: string; done: boolean; pinned?: boolean }

/** 📌 kiinnitetty tiedote (backendin pinned-lippu; varalla tekstin 📌-alku) */
function isPinned(t: TodayTask): boolean {
  return t.pinned === true || t.text.startsWith('📌');
}
const CACHE_KEY = 'savroc-today-bar';
const POLL_MS = 5 * 60 * 1000;

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function TodayBar() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<TodayTask[] | null>(() => {
    try {
      const c = JSON.parse(sessionStorage.getItem(CACHE_KEY) ?? 'null');
      return c && c.date === todayIso() ? c.tasks : null;
    } catch { return null; }
  });

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await authFetch('/info-display');
        if (!r.ok || !alive) return;
        const d = await r.json();
        const day = (d.week?.days ?? []).find((x: { date: string }) => x.date === todayIso());
        const t: TodayTask[] = (day?.tasks ?? []).map((x: TodayTask) => ({
          text: x.text, done: x.done, ...(x.pinned ? { pinned: true } : {}),
        }));
        if (!alive) return;
        setTasks(t);
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ date: todayIso(), tasks: t }));
      } catch { /* säilytä edellinen */ }
    };
    void load();
    const id = setInterval(() => void load(), POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (tasks == null || tasks.length === 0) return null;

  // 20.8.2026: palkki oli tekstiseinä. Se listasi päivän kaikki työt, jokaisen
  // omalla emojillaan, ja piti yliviivatut valmiit työt näkyvissä — puhelimella
  // se vei yli kolmanneksen ruudusta ennen kuin varsinainen sivu alkoi.
  //
  // Tehtyä ei tarvitse näyttää: valmiit tiivistyvät laskuriksi ("3 tehty").
  // Avoimista näytetään kolme ja loput lukuna — palkin tehtävä on kertoa
  // päivän suunta yhdellä silmäyksellä, ei olla tehtävälista. Koko lista on
  // yhden napautuksen päässä infonäytöllä.
  //
  // 📌 Kiinnitetyt tiedotteet (1.10.2026) eivät vie päivän töiden paikkoja:
  // niistä näytetään yksi (uusin, katkaistuna) + "+N 📌" omana lohkonaan
  // ennen töitä. Koko lista ja loput kiinnitetyt ovat infonäytöllä.
  const pinned = tasks.filter(isPinned);
  const work = tasks.filter(t => !isPinned(t));
  const open = work.filter(t => !t.done);
  const doneCount = work.length - open.length;
  // Kaksi työtä mahtuu 390 px:n leveydelle yhdelle–kahdelle riville; kolmas
  // työnsi laskurin omalle kolmannelle rivilleen.
  const shown = open.slice(0, 2);
  const rest = open.length - shown.length;

  return (
    <button onClick={() => navigate('/infonaytto')} title="Avaa infonäyttö"
      style={{
        // Leveyskuri (19.8.2026): border-box + minWidth 0 + maxWidth — ilman
        // näitä nowrap-teksti + padding levensi koko sivun mobiililla
        display: 'block', width: '100%', maxWidth: '100%', minWidth: 0,
        boxSizing: 'border-box', textAlign: 'left', cursor: 'pointer',
        background: 'linear-gradient(90deg, rgba(14,122,110,.10), transparent)',
        border: 'none', borderBottom: '1px solid rgba(14,122,110,.25)',
        padding: '7px 14px', font: 'inherit', fontSize: 13, color: 'inherit',
        overflow: 'hidden',
      }}>
      <span style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'baseline',
        gap: '3px 10px', minWidth: 0,
      }}>
        <b style={{ color: '#0e7a6e', flexShrink: 0 }}>Tänään:</b>
        {pinned.length > 0 && (
          <span data-testid="todaybar-pinned" title={pinned.map(p => p.text).join(' · ')}
            style={{ display: 'inline-flex', gap: 4, minWidth: 0, maxWidth: '100%' }}>
            <span style={{
              minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              maxWidth: 'min(52ch, 100%)',
            }}>
              {pinned[0].text}
            </span>
            {pinned.length > 1 && (
              <span style={{ color: '#64748b', flexShrink: 0 }}>+{pinned.length - 1} 📌</span>
            )}
          </span>
        )}
        {work.length === 0 ? null : shown.length > 0
          ? shown.map((t, i) => (
            <span key={i} style={{ minWidth: 0 }}>
              {t.text}{i < shown.length - 1 ? ' ·' : ''}
            </span>
          ))
          : <span style={{ color: '#64748b' }}>kaikki päivän työt tehty</span>}
        {rest > 0 && <span style={{ color: '#64748b' }}>+{rest}</span>}
        {doneCount > 0 && shown.length > 0 && (
          <span style={{ color: '#64748b' }}>· {doneCount} tehty</span>
        )}
      </span>
    </button>
  );
}
